// Vehicle Dashboard (Quick View) — Dashboard-page-style info & stats sheet:
// one consolidated API call (/fleet/vehicles/{id}/dashboard, TTL-cached server
// side), coloured KPI tiles, equal-width command buttons, charts + 7-day status
// donut, telemetry under charts, maintenance table, Trips & Events side by side.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip as ReTooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  Loader2, MapPin, Route, BookOpen, ClipboardList, Key, Gauge, Clock, Send,
  AlertTriangle, Pencil, Bell, Users, Wrench, ExternalLink, Camera,
  Timer, TrendingUp, TrendingDown, Map as MapIcon, RefreshCw, IdCard, UserX, UserCog,
  UserRound, Truck, Container, Navigation, Leaf, UserCheck, Video, History, BookUser,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { deviceDisplayName } from '../../utils/deviceName';
import { formatDT, formatTimeAgo, formatDuration, formatSpeed, formatDistance, resolveTimezone, getZonedParts, zonedDateTimeToUtc, formatWallDateOnly } from '../../utils/timezone';
import { computeDisplayValue, flattenIoElements } from '../../utils/ioMappingValue';
import { getIoValueColorClass } from '../../utils/ioValueColors';
import { OccupancyPill } from './OccupancyPill';
import { DrivingBehaviourHelp } from './DrivingBehaviourHelp';
import { ReadOnlyAttachmentsList, VEHICLE_DOC_TYPES } from '../shared/Attachments';
import { Paperclip, Download, Upload, Phone, Mail, MessageCircle, Satellite } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '../ui/hover-card';
import { exportDashboardPdf, exportDashboardHtml, safeName } from '../../utils/tripExport';
import { OCCUPANCY_BADGE_CLS } from '../../utils/occupancy';
import { assetTypeLabel } from '../live-tracking/EditDeviceDialog';
import { VehicleVideoCardContent } from './VehicleVideoCard';
import { useIsMobileOrWebview } from '../../hooks/useIsMobileOrWebview';

// Command button colours — EXACT match to the Live Tracking page (TelemetryPanel CMD_BG).
const CMD_BG = {
  blue: 'bg-blue-600 hover:bg-blue-700', red: 'bg-red-600 hover:bg-red-700',
  green: 'bg-green-600 hover:bg-green-700', yellow: 'bg-yellow-500 hover:bg-yellow-600',
  orange: 'bg-orange-500 hover:bg-orange-600', purple: 'bg-purple-600 hover:bg-purple-700',
  gray: 'bg-gray-500 hover:bg-gray-600', slate: 'bg-gray-500 hover:bg-gray-600',
  teal: 'bg-teal-600 hover:bg-teal-700', pink: 'bg-pink-600 hover:bg-pink-700',
  indigo: 'bg-indigo-600 hover:bg-indigo-700', cyan: 'bg-cyan-600 hover:bg-cyan-700',
  lime: 'bg-lime-600 hover:bg-lime-700', rose: 'bg-rose-600 hover:bg-rose-700',
};
const DONUT_COLORS = { Moving: '#10b981', Idle: '#f59e0b', Stopped: '#3b82f6', 'Parked/Offline': '#94a3b8' };
const OCC_LABEL = { staff: 'Staff', passenger: 'Passenger', trailer: 'Trailer', equipment: 'Equipment' };
const CREW_COLL = { driver: 'drivers', staff: 'staff', passenger: 'passengers' };

// Square person photo tile (same style as the People list) with click-to-enlarge viewer.
const CrewPhotoTile = ({ api, person, index, onNameClick, onNavigate }) => {
  const { features } = useFeatureFlags();
  const { user: currentUser } = useAuth();
  const [photoUrl, setPhotoUrl] = useState(null);
  const [details, setDetails] = useState(null);
  const [viewer, setViewer] = useState(false);
  useEffect(() => {
    let obj; let cancelled = false;
    setPhotoUrl(null);
    setDetails(null);
    const coll = CREW_COLL[person.person_type] || CREW_COLL[person.role] || 'drivers';
    if (!person.person_id) return undefined;
    api.get(`/${coll}/${person.person_id}`)
      .then((r) => {
        if (cancelled) return;
        setDetails(r.data || null);
        const u = r.data?.photo?.url;
        if (!u) return;
        api.get(u.replace(/^\/api/, ''), { responseType: 'blob' })
          .then((b) => { if (!cancelled) { obj = URL.createObjectURL(b.data); setPhotoUrl(obj); } })
          .catch(() => {});
      })
      .catch(() => {});
    return () => { cancelled = true; if (obj) URL.revokeObjectURL(obj); };
  }, [person.person_id, person.person_type, person.role, api]);
  const initials = (person.name || '?').split(' ').filter(Boolean).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  const roleLabel = person.role === 'driver' ? 'Driver' : (OCC_LABEL[person.role] || person.role);
  const phone = details?.phone || details?.mobile || null;
  const email = details?.email || null;
  const empId = details?.employee_id || null;
  // Chat gate — same rule as PersonProfileDrawer: linked users always chat-able,
  // person chat logins need an active (non-pending/deactivated/expired) account.
  const chatTargetId = details?.linked_user_id || details?.chat_user_id;
  const canMessage = features?.pages?.chat_page === true && !!chatTargetId
    && chatTargetId !== currentUser?.id
    && (details?.linked_user_id
      ? true
      : (!!details?.account_enabled && !!details?.messaging_enabled
        && !['pending_invitation', 'deactivated', 'expired'].includes(details?.chat_invite_status)));
  const openChat = async () => {
    try {
      const r = await api.post('/conversations', { type: 'direct', participants: [chatTargetId] });
      onNavigate(`/chat?conversation=${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not open chat');
    }
  };
  const linkCls = 'flex items-center gap-1.5 text-xs text-[#2563eb] dark:text-blue-400 hover:underline';
  const contactRows = (prefix) => (
    <>
      {empId && <p className="text-[11px] text-muted-foreground">ID: {empId}</p>}
      {phone ? (
        <a href={`tel:${phone}`} className={linkCls} data-testid={`${prefix}-phone-${index}`}><Phone className="w-3 h-3 shrink-0" /> {phone}</a>
      ) : (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><Phone className="w-3 h-3 shrink-0" /> No phone on file</p>
      )}
      {email && <a href={`mailto:${email}`} className={`${linkCls} truncate`} data-testid={`${prefix}-email-${index}`}><Mail className="w-3 h-3 shrink-0" /> <span className="truncate">{email}</span></a>}
      {canMessage && (
        <Button size="sm" className="h-7 w-full mt-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={openChat} data-testid={`${prefix}-chat-${index}`}>
          <MessageCircle className="w-3.5 h-3.5 mr-1.5" /> Chat
        </Button>
      )}
    </>
  );
  const photoEl = photoUrl ? (
    <button type="button" onClick={() => setViewer(true)} title="View photo & details" className="block rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563eb]" data-testid={`crew-photo-btn-${index}`}>
      <img src={photoUrl} alt={person.name} className="w-16 h-16 rounded-lg object-cover border cursor-pointer" data-testid={`crew-photo-img-${index}`} />
    </button>
  ) : (
    <button type="button" onClick={() => setViewer(true)} title="View details" className="w-16 h-16 rounded-lg bg-[#2563eb]/10 text-[#2563eb] dark:bg-blue-400/10 dark:text-blue-400 flex items-center justify-center text-lg font-semibold cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563eb]" data-testid={`crew-photo-placeholder-${index}`}>{initials}</button>
  );
  return (
    <div className={`flex flex-col items-center gap-1 w-24 ${person.last ? 'opacity-70' : ''}`} data-testid={`crew-photo-tile-${index}`}>
      <HoverCard openDelay={200} closeDelay={100}>
        <HoverCardTrigger asChild>{photoEl}</HoverCardTrigger>
        <HoverCardContent side="top" align="center" className="w-60 z-[10001] p-3" data-testid={`crew-hover-card-${index}`}>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold leading-tight">{person.name}</p>
              <Badge variant="outline" className={`text-[9px] border-0 shrink-0 ${OCCUPANCY_BADGE_CLS[person.role] || OCCUPANCY_BADGE_CLS.passenger}`}>{roleLabel}</Badge>
            </div>
            {contactRows('crew-hover')}
          </div>
        </HoverCardContent>
      </HoverCard>
      {onNameClick ? (
        <button type="button" onClick={onNameClick} title={`Open ${person.name}'s profile`} className="text-[11px] font-medium text-center leading-tight w-full break-words line-clamp-2 text-[#2563eb] dark:text-blue-400 hover:underline focus:outline-none focus-visible:ring-1 focus-visible:ring-[#2563eb] rounded" data-testid={`crew-photo-name-${index}`}>{person.name}</button>
      ) : (
        <span className="text-[11px] font-medium text-center leading-tight w-full break-words line-clamp-2" title={person.name} data-testid={`crew-photo-name-${index}`}>{person.name}</span>
      )}
      <Badge variant="outline" className={`text-[9px] border-0 ${OCCUPANCY_BADGE_CLS[person.role] || OCCUPANCY_BADGE_CLS.passenger}`}>{roleLabel}</Badge>
      <Dialog open={viewer} onOpenChange={setViewer}>
        <DialogContent className="max-w-xl" data-testid="crew-photo-viewer">
          <DialogHeader>
            <DialogTitle>{person.name}</DialogTitle>
            <DialogDescription>{roleLabel}{photoUrl ? ' photo & details' : ' details'}</DialogDescription>
          </DialogHeader>
          {photoUrl ? (
            <img src={photoUrl} alt={person.name} className="w-full max-h-[55vh] object-contain rounded-md border" data-testid="crew-photo-viewer-img" />
          ) : (
            <div className="w-24 h-24 mx-auto rounded-lg bg-[#2563eb]/10 text-[#2563eb] dark:bg-blue-400/10 dark:text-blue-400 flex items-center justify-center text-3xl font-semibold" data-testid="crew-photo-viewer-placeholder">{initials}</div>
          )}
          <div className="space-y-1.5 pt-1 border-t" data-testid="crew-photo-viewer-details">
            <div className="flex items-center justify-between gap-2 pt-1.5">
              <p className="text-sm font-semibold leading-tight">{person.name}</p>
              <Badge variant="outline" className={`text-[9px] border-0 shrink-0 ${OCCUPANCY_BADGE_CLS[person.role] || OCCUPANCY_BADGE_CLS.passenger}`}>{roleLabel}</Badge>
            </div>
            {contactRows('crew-viewer')}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
const OCC_ROW_META = {
  staff: { icon: UserRound, label: 'Staff' },
  passenger: { icon: Users, label: 'Passengers' },
  trailer: { icon: Truck, label: 'Trailer' },
  equipment: { icon: Container, label: 'Equipment' },
};
const TIMELINE_COLORS = { driver: '#10b981', staff: '#3b82f6', passenger: '#8b5cf6', trailer: '#f59e0b', equipment: '#0ea5e9' };
const TIMELINE_LABEL = { driver: 'Driver', staff: 'Staff', passenger: 'Passenger', trailer: 'Trailer', equipment: 'Equipment' };
const TRIP_OVERLAY_COLOR = 'rgba(37, 99, 235, 0.18)';
// Theme-aware recharts tooltip (dark-mode readable) + a subtle hover cursor only a few shades off the card background.
const TT_PROPS = {
  contentStyle: { backgroundColor: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' },
  labelStyle: { color: 'hsl(var(--foreground))', fontWeight: 600 },
  itemStyle: { color: 'hsl(var(--foreground))' },
  wrapperStyle: { zIndex: 50, outline: 'none' },
};
const BAR_CURSOR = { fill: 'hsl(var(--muted-foreground))', fillOpacity: 0.15 };

const chip = 'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium';
const timeAgoCap = (d) => { const s = formatTimeAgo(d); return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; };
const parseTs = (t) => { try { return new Date(String(t).replace('Z', '+00:00')).getTime(); } catch { return 0; } };

// Dashboard-page-style KPI card with a coloured icon tile.
const KpiCard = ({ label, value, sub, icon: Icon, color, delta, testId }) => (
  <Card className="hover:shadow-md transition-shadow" data-testid={testId}>
    <CardContent className="p-3.5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
          <p className="text-lg font-bold leading-tight mt-0.5">{value}</p>
          {sub && <p className="text-[11px] text-muted-foreground truncate" data-testid={`${testId}-sub`}>{sub}</p>}
          {delta != null && (
            <div className="flex items-center gap-1 mt-1" data-testid={`${testId}-delta`}>
              {delta >= 0 ? <TrendingUp className="w-3 h-3 text-green-500" /> : <TrendingDown className="w-3 h-3 text-red-500" />}
              <span className={`text-[11px] ${delta >= 0 ? 'text-green-500' : 'text-red-500'}`}>{delta > 0 ? '+' : ''}{delta}% vs prev 7d</span>
            </div>
          )}
        </div>
        <div className={`p-2 rounded-lg shrink-0 ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const SectionCard = ({ icon: Icon, title, right, children, testId, className }) => (
  <Card data-testid={testId} className={className}>
    <CardHeader className="py-3 px-4 flex flex-row items-center justify-between space-y-0">
      <CardTitle className="text-sm flex items-center gap-2"><Icon className="w-4 h-4 text-[#2563eb]" /> {title}</CardTitle>
      {right}
    </CardHeader>
    <CardContent className="px-4 pb-4 pt-0">{children}</CardContent>
  </Card>
);

export const VehicleQuickView = ({ device, open, onClose, onOpenDriverLogbook, onOpenAssetLogbook, onEdit, svcStatus, onOpenTravelHistory }) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { isSubpageEnabled, isFeatureEnabled } = useFeatureFlags();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tripsLimit, setTripsLimit] = useState('5');
  const [eventsLimit, setEventsLimit] = useState('10');
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
  const [sendConfirmCmd, setSendConfirmCmd] = useState(null);
  const [sendingCommand, setSendingCommand] = useState(false);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [vehicleDocs, setVehicleDocs] = useState([]);
  const [assignHistory, setAssignHistory] = useState([]);
  const [assignHistoryExpanded, setAssignHistoryExpanded] = useState(false);
  // Device id of the currently displayed payload — lets a manual Refresh keep the
  // on-screen data (no flicker back to the stale open-time snapshot) while only a
  // genuine device switch clears it.
  const loadedDeviceRef = useRef(null);

  const canTelemetry = isSubpageEnabled('live_tracking_telemetry_data');
  const canCommands = isSubpageEnabled('live_tracking_commands');
  const isMobileWebview = useIsMobileOrWebview();
  const actBtn = isMobileWebview ? 'h-11 px-4 text-sm font-semibold' : 'h-7';
  const actIcon = isMobileWebview ? 'w-5 h-5 mr-2' : 'w-3.5 h-3.5 mr-1.5';
  const canEvents = isSubpageEnabled('live_tracking_events');
  const canGreenDriving = canEvents && isSubpageEnabled('fleet_green_driving');
  const canLiveVideo = isFeatureEnabled('media_page') && isSubpageEnabled('media_live_videos');
  const canEventClips = isFeatureEnabled('media_page') && isSubpageEnabled('media_event_videos');
  const showVideoCard = device?.device_type === 'dashcam' && device?.has_video === true && (canLiveVideo || canEventClips);

  useEffect(() => {
    if (!open || !device) return;
    let cancelled = false;
    // Manual Refresh (same device): behave like the 60s auto-refresh — keep the current
    // payload visible while fetching so the occupancy/driver cards never fall back to
    // the stale snapshot captured when the dashboard was opened.
    const keepData = loadedDeviceRef.current === device.id;
    const load = (freshArg, showSpinner) => {
      if (showSpinner) { setLoading(true); if (!keepData) setData(null); }
      api.get(`/fleet/vehicles/${device.id}/dashboard?days=14&fresh=${freshArg ? 1 : 0}`)
        .then((r) => { if (!cancelled) { setData(r.data); setLastUpdated(Date.now()); loadedDeviceRef.current = device.id; } })
        .catch(() => { if (!cancelled && showSpinner && !keepData) setData({ trips: [], events: [], driver_sessions: [], beacon_occupants: [], asset_bindings: [], maintenance: null }); })
        .finally(() => { if (!cancelled && showSpinner) setLoading(false); });
    };
    load(refreshNonce > 0, true);
    const iv = setInterval(() => load(true, false), 60000);
    return () => { cancelled = true; clearInterval(iv); };
    // device?.id (not the object): Fleet's 60s snapshot sync swaps the prop object —
    // that must refresh the fallback merge only, never re-trigger a spinner fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, device?.id, api, refreshNonce]);

  useEffect(() => {
    if (!open || !device?.id) { setVehicleDocs([]); return undefined; }
    let cancelled = false;
    api.get(`/fleet/vehicles/${device.id}/attachments`)
      .then((r) => { if (!cancelled) setVehicleDocs(r.data?.attachments || []); })
      .catch(() => {});
    api.get(`/fleet/vehicles/${device.id}/assignment-history?limit=20`)
      .then((r) => { if (!cancelled) setAssignHistory(r.data?.history || []); })
      .catch(() => { if (!cancelled) setAssignHistory([]); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, device?.id, api, refreshNonce]);

  useEffect(() => {
    if (!open || !device?.vehicle_photo?.url) { setPhotoUrl(null); return; }
    let cancelled = false; let objUrl = null;
    api.get(device.vehicle_photo.url.replace(/^\/api/, ''), { responseType: 'blob' })
      .then((r) => { if (!cancelled) { objUrl = URL.createObjectURL(r.data); setPhotoUrl(objUrl); } })
      .catch(() => {});
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, device?.vehicle_photo?.url, api]);

  const trips = useMemo(() => data?.trips || [], [data]);
  const visibleTrips = useMemo(() => trips.slice(0, Number(tripsLimit)), [trips, tripsLimit]);
  const events = useMemo(() => (data?.events || []).slice(0, Number(eventsLimit)), [data, eventsLimit]);
  // Live device fields refresh with the dashboard payload (60s auto-refresh).
  // Newest-wins merge: the Fleet list prop can be fresher (live WS updates)
  // than the payload snapshot — never let older data override newer.
  const dev = useMemo(() => {
    const live = data?.device_live;
    if (!live) return device || {};
    const propTs = parseTs(device?.last_seen || device?.last_gps_time);
    const liveTs = parseTs(live.last_seen || live.last_gps_time);
    return propTs > liveTs ? { ...live, ...device } : { ...device, ...live };
  }, [device, data]);

  const stats = data?.stats;
  const weekStats = useMemo(() => ({
    trips: stats?.trips_7d ?? 0,
    distance: stats?.distance_7d ?? 0,
    delta: stats && stats.distance_prev_7d > 0
      ? Math.round(((stats.distance_7d - stats.distance_prev_7d) / stats.distance_prev_7d) * 100)
      : null,
  }), [stats]);

  const dailySeries = useMemo(() => (stats?.daily || []).map((d) => ({
    ...d,
    label: new Date(`${d.date}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short' }),
  })), [stats]);

  const statusDonut = useMemo(() => {
    const s = stats?.status_secs || {};
    return [
      { name: 'Moving', value: Math.round((s.moving || 0) / 3600 * 10) / 10 },
      { name: 'Idle', value: Math.round((s.idle || 0) / 3600 * 10) / 10 },
      { name: 'Stopped', value: Math.round((s.stopped || 0) / 3600 * 10) / 10 },
      { name: 'Parked/Offline', value: Math.round((s.offline || 0) / 3600 * 10) / 10 },
    ].filter((x) => x.value > 0);
  }, [stats]);

  const tripOccupants = useMemo(() => {
    const ds = data?.driver_sessions || [];
    const bo = data?.beacon_occupants || [];
    const ab = data?.asset_bindings || [];
    return (trip) => {
      const ts = parseTs(trip.start_time); const te = parseTs(trip.end_time);
      const overlaps = (s, e) => parseTs(s) <= te && (e ? parseTs(e) : Infinity) >= ts;
      const drivers = ds.filter((s) => overlaps(s.started_at, s.ended_at));
      const driverIds = new Set(drivers.map((d) => d.person_id));
      const driverNames = new Set(drivers.map((d) => d.person_name));
      const pax = bo.filter((o) => overlaps(o.first_seen, o.last_seen) && !driverIds.has(o.person_id));
      const seen = new Set();
      // Trailers / Equipment / binding-detected Staff & Passengers during the trip window
      const bindSeen = new Set();
      const bindings = ab
        .filter((b) => overlaps(b.started_at, b.ended_at) && b.beacon_name && !driverNames.has(b.beacon_name))
        .filter((b) => { const k = `${b.entity_type}:${b.beacon_name}`; if (bindSeen.has(k)) return false; bindSeen.add(k); return true; });
      const paxNames = new Set(pax.map((o) => o.person_name));
      return {
        drivers: drivers.filter((d) => !seen.has(d.person_id) && seen.add(d.person_id)),
        occupants: pax.filter((o) => !seen.has(o.person_id) && seen.add(o.person_id)),
        bindings: bindings.filter((b) => !(['staff', 'passenger'].includes(b.entity_type) && paxNames.has(b.beacon_name))),
      };
    };
  }, [data]);

  // Live Occupancy — mirrors the Fleet card exactly: per type, current bindings
  // win; otherwise fall back to the last trip's bindings with a dimmed "Last:".
  const occupancy = useMemo(() => {
    const ignitionOn = dev.ignition_on === true;
    const norm = (arr) => (arr || []).map((o) => (typeof o === 'string' ? { name: o } : o));
    const cb = dev.current_bindings || {};
    const lb = dev.last_bindings || {};
    const session = dev.active_driver_session || dev.current_driver_session;
    const currentDriver = ignitionOn && session?.person_name ? session : null;
    const manualDriver = !currentDriver && dev.manual_assigned_driver ? dev.manual_assigned_driver : null;
    const lastDriver = dev.last_driver_session?.person_name ? dev.last_driver_session : null;
    const rows = ['staff', 'passenger', 'trailer', 'equipment']
      .map((k) => ({ k, items: norm(cb[k]), lastItems: norm(lb[k]) }))
      .filter((r) => r.items.length > 0 || r.lastItems.length > 0);
    const identityEnabled = !!dev.identity_logbook?.enabled;
    return {
      ignitionOn,
      currentDriver,
      manualDriver,
      lastDriver,
      rows,
      identityEnabled,
      offline: dev.status === 'offline',
      endedAt: dev.last_trip_ended_at,
      hasAny: !!currentDriver || !!manualDriver || !!lastDriver || rows.length > 0 || (ignitionOn && identityEnabled),
    };
  }, [dev]);

  // "Last Trip" = the last COMPLETED trip. While ignition is ON the newest trip is
  // still accumulating (flagged in_progress by the server) and must be excluded.
  const lastTrip = useMemo(() => trips.find((t) => t.end_time && !t.in_progress), [trips]);
  // Crew & Occupants photo card: current people aboard, else last trip's people (persons only).
  const crew = useMemo(() => {
    const people = [];
    const seen = new Set();
    const push = (p, role, isLast) => {
      const name = p?.person_name || p?.name;
      if (!name) return;
      const key = p.person_id || `${role}-${name}`;
      if (seen.has(key)) return;
      seen.add(key);
      people.push({ person_id: p.person_id, person_type: p.person_type, role, name, last: isLast });
    };
    const personRows = occupancy.rows.filter((r) => r.k === 'staff' || r.k === 'passenger');
    const hasCurrent = !!occupancy.currentDriver || personRows.some((r) => r.items.length > 0);
    if (hasCurrent) {
      push(occupancy.currentDriver, 'driver', false);
      personRows.forEach((r) => r.items.forEach((o) => push(o, r.k, false)));
    } else {
      push(occupancy.lastDriver, 'driver', true);
      personRows.forEach((r) => r.lastItems.forEach((o) => push(o, r.k, true)));
    }
    return people;
  }, [occupancy]);
  // Current Trip card: trip METRICS only while ignition is ON (occupants stay in Live Occupancy).
  // The trips list can lag 60s behind (server cache) — if ignition is ON but the new
  // trip row hasn't landed yet, synthesize it from the always-fresh live device fields.
  const currentTrip = useMemo(() => {
    const flagged = trips.find((t) => t.in_progress);
    if (flagged) return flagged;
    if (dev.ignition_on !== true || !dev.last_trip_started_at) return null;
    const newest = trips[0];
    if (newest && String(newest.end_time || newest.start_time || '') >= String(dev.last_trip_started_at)) return null;
    return { start_time: dev.last_trip_started_at, synthetic: true };
  }, [trips, dev]);
  // Green Driving: harsh event counts over the 7-day stats window (events privilege gated).
  const harsh = useMemo(() => data?.harsh_7d || {}, [data]);
  const harshTotal = (harsh.harsh_braking || 0) + (harsh.harsh_acceleration || 0) + (harsh.harsh_cornering || 0);
  const behaviour = stats?.driving_behaviour || null;
  const hbRating = behaviour?.rating || 'rated';
  const hbScore = hbRating === 'not_rated' ? null : (behaviour?.score ?? (harshTotal === 0 ? 100 : null));

  // PDF / HTML dashboard export — same branded look as the History page exports.
  const exportDashboard = (fmt) => {
    const dash = (v) => (v == null || v === '' ? '-' : String(v));
    const two = { columns: [{ label: 'Field', weight: 1 }, { label: 'Value', weight: 2.5 }] };
    const sections = [{
      heading: 'Vehicle Details', ...two,
      rows: [
        ['Name', deviceDisplayName(device)], ['Registration / IMEI', dash(device.registration || device.imei)],
        ['Type', dash(device.asset_type)], ['Make / Model', device.asset_make ? `${device.asset_make} ${device.asset_model || ''}${device.asset_year ? ` · ${device.asset_year}` : ''}` : '-'],
        ['Status', device.is_active ? 'Active' : 'Inactive'],
        ['Odometer', device.computed_odometer != null ? formatDistance(device.computed_odometer, { decimals: 1 }) : '-'],
        ['Engine hours', device.computed_engine_hours != null ? `${Number(device.computed_engine_hours).toFixed(1)} h` : '-'],
      ],
    }];
    if (stats) {
      sections.push({
        heading: 'Last 7 Days', ...two,
        rows: [
          ['Distance', `${stats.distance_7d ?? 0} km`], ['Trips', String(stats.trips_7d ?? 0)],
          // Green Driving rows only when the privilege is granted (matches the dashboard card)
          ...(canGreenDriving ? [
            ['Driving Behaviour score', behaviour ? (hbRating === 'not_rated' ? 'Not yet rated' : `${behaviour.score}${hbRating === 'low_confidence' ? ' (low confidence)' : ''}`) : '-'],
            ['Harsh events', String(behaviour?.total ?? 0)],
          ] : []),
        ],
      });
    }
    if ((trips || []).length) {
      sections.push({
        heading: 'Recent Trips',
        columns: [{ label: 'Start', weight: 1 }, { label: 'End', weight: 1 }, { label: 'Distance', weight: 0.6 }, { label: 'Duration', weight: 0.6 }],
        rows: trips.slice(0, 25).map((t) => [
          t.start_time ? formatDT(t.start_time) : '-', t.end_time ? formatDT(t.end_time) : 'ongoing',
          t.distance_km != null ? `${Number(t.distance_km).toFixed(1)} km` : '-',
          t.duration_secs != null ? formatDuration(t.duration_secs) : '-',
        ]),
      });
    }
    if ((events || []).length) {
      sections.push({
        heading: 'Recent Events',
        columns: [{ label: 'Event', weight: 1.6 }, { label: 'Status', weight: 0.6 }, { label: 'Time', weight: 1 }],
        rows: events.map((evt) => [evt.event_rule_name || evt.event_type || '-', dash(evt.status), evt.triggered_at ? formatDT(evt.triggered_at) : '-']),
      });
    }
    if (vehicleDocs.length) {
      sections.push({
        heading: 'Documents',
        columns: [{ label: 'Document', weight: 2 }, { label: 'Type', weight: 1 }, { label: 'Expiry', weight: 1 }],
        rows: vehicleDocs.map((a) => [a.name || 'Attachment', a.doc_type || '-', a.expiry_date ? formatWallDateOnly(a.expiry_date) : '-']),
      });
    }
    const title = `${deviceDisplayName(device)} — Vehicle Dashboard`;
    const subtitle = `${device.registration || device.imei || ''} · exported ${formatDT(Date.now())}`;
    const fname = `${safeName(deviceDisplayName(device))}-dashboard.${fmt}`;
    if (fmt === 'pdf') {
      exportDashboardPdf(title, subtitle, sections, fname).catch(() => toast.error('Export failed'));
      return;
    }    const opts = {
      kpis: [
        ...(dev.odometer_source && dev.odometer_source !== 'off' ? [{ label: 'Odometer', value: device.computed_odometer != null ? formatDistance(device.computed_odometer, { decimals: 1 }) : '—', accent: 'blue' }] : []),
        ...(dev.engine_hours_source && dev.engine_hours_source !== 'off' ? [{ label: 'Engine Hours', value: device.computed_engine_hours != null ? `${Number(device.computed_engine_hours).toFixed(1)} h` : '—', accent: 'green' }] : []),
        { label: 'Trips (7d)', value: String(stats?.trips_7d ?? 0), accent: 'purple' },
        { label: 'Distance (7d)', value: `${stats?.distance_7d ?? 0} km`, accent: 'amber' },
      ],
      score: canGreenDriving && (behaviour || hbScore != null) ? {
        heading: 'Driving Behaviour — Last 7 Days',
        value: hbScore,
        rating: hbRating,
        note: hbRating === 'low_confidence' ? `Low confidence — ${stats?.distance_7d ?? 0} km driven in the last 7 days.` : null,
        legend: [
          { label: 'Harsh Braking', count: harsh.harsh_braking || 0, color: '#ef4444' },
          { label: 'Harsh Acceleration', count: harsh.harsh_acceleration || 0, color: '#f59e0b' },
          { label: 'Harsh Cornering', count: harsh.harsh_cornering || 0, color: '#8b5cf6' },
        ],
      } : null,
      charts: (stats?.daily || []).length ? [
        {
          heading: 'Daily Distance (km) — Last 7 Days', color: '#2563eb',
          bars: stats.daily.map((d) => ({ label: new Date(`${d.date}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short' }), value: d.km })),
        },
        {
          heading: 'Daily Driving Time (mins) — Last 7 Days', color: '#0d9488',
          bars: stats.daily.map((d) => ({ label: new Date(`${d.date}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short' }), value: d.mins })),
        },
      ] : [],
      donut: statusDonut.length ? {
        heading: 'Status Distribution (hrs) — Last 7 Days',
        unit: 'h',
        slices: statusDonut.map((s) => ({ label: s.name, value: s.value, color: DONUT_COLORS[s.name] })),
      } : null,
    };
    const doHtml = async () => {
      if (showVideoCard) {
        const cams = Number(dev.total_cameras) > 0 ? Math.floor(Number(dev.total_cameras)) : 1;
        const names = dev.channel_names || {};
        let clips = [];
        try {
          const res = await api.get('/media/event-videos', { params: { device_id: device.id, limit: 3 } });
          clips = (res.data?.items || []).map((c) => ({
            label: String(c.event_label_display || c.event_label || c.event_type || 'Event clip').replace(/\s*\(\d{1,5}\)\s*$/u, '').trim(),
            channel: names[String(c.channel)] || `CH${c.channel ?? '?'}`,
            time: c.created_at ? formatDT(c.created_at) : '-',
          }));
        } catch { /* clips optional in export */ }
        opts.video = { cameras: cams, clips };
      }
      await exportDashboardHtml(title, subtitle, sections, fname, opts);
    };
    doHtml().catch(() => toast.error('Export failed'));
  };
  const hbScoreCls = hbScore == null ? 'text-muted-foreground'
    : hbScore >= 85 ? 'text-emerald-600 dark:text-emerald-400'
    : hbScore >= 60 ? 'text-amber-600 dark:text-amber-400'
    : 'text-red-600 dark:text-red-400';
  const HB_COLORS = { harsh_braking: '#ef4444', harsh_acceleration: '#f59e0b', harsh_cornering: '#8b5cf6' };
  const hbDonut = useMemo(() => (harshTotal === 0
    ? [{ name: 'Clean', key: 'clean', value: 1 }]
    : [
        { name: 'Harsh Braking', key: 'harsh_braking', value: harsh.harsh_braking || 0 },
        { name: 'Harsh Acceleration', key: 'harsh_acceleration', value: harsh.harsh_acceleration || 0 },
        { name: 'Harsh Cornering', key: 'harsh_cornering', value: harsh.harsh_cornering || 0 },
      ].filter((d) => d.value > 0)), [harsh, harshTotal]);
  const hbDaily = useMemo(() => (behaviour?.daily || []).map((d) => ({
    ...d,
    label: new Date(`${d.date}T00:00:00`).toLocaleDateString(undefined, { weekday: 'short' }),
  })), [behaviour]);

  // ── Occupancy Timeline (day selector over the 14-day window) ──
  // All day boundaries use the timezone.js standard (resolveTimezone +
  // zonedDateTimeToUtc, DST-safe) — correct in ANY browser timezone.
  const [timelineDay, setTimelineDay] = useState(null);
  const timelineDays = useMemo(() => {
    const p = getZonedParts(new Date(), { timezone: resolveTimezone() });
    const base = Date.UTC(p.year, p.month - 1, p.day, 12);
    return Array.from({ length: 14 }, (_, i) => {
      const v = new Date(base - i * 86400000).toISOString().slice(0, 10);
      const label = i === 0 ? 'Today' : i === 1 ? 'Yesterday'
        : `${new Date(`${v}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short' })} ${formatWallDateOnly(v)}`;
      return { v, label };
    });
  }, []);
  const tDay = timelineDay || timelineDays[0]?.v;

  const timelineRange = useMemo(() => {
    if (!tDay) return null;
    const [y, m, d] = tDay.split('-').map(Number);
    const start = zonedDateTimeToUtc({ year: y, month: m, day: d }, resolveTimezone())?.getTime();
    return start != null ? { start, end: start + 86400000 } : null;
  }, [tDay]);

  const timelineRows = useMemo(() => {
    if (!data || !timelineRange) return [];
    const { start: dayStart, end: dayEnd } = timelineRange;
    const nowMs = Date.now();
    const map = new Map();
    const add = (type, name, s, e) => {
      if (!name || !s) return;
      const a = Math.max(parseTs(s), dayStart);
      const b = Math.min(e ? parseTs(e) : nowMs, dayEnd, nowMs);
      if (b <= a) return;
      const key = `${type}:${name}`;
      if (!map.has(key)) map.set(key, { type, name, segs: [] });
      map.get(key).segs.push([a, b]);
    };
    (data.driver_sessions || []).forEach((s) => add('driver', s.person_name, s.started_at, s.ended_at));
    (data.beacon_occupants || []).forEach((o) => add(o.person_type || 'passenger', o.person_name, o.first_seen, o.last_seen));
    (data.asset_bindings || []).forEach((b) => add(b.entity_type, b.beacon_name, b.started_at, b.ended_at));
    const order = { driver: 0, staff: 1, passenger: 2, trailer: 3, equipment: 4 };
    return [...map.values()]
      .map((r) => ({
        ...r,
        total: r.segs.reduce((acc, [s, e]) => acc + (e - s), 0),
        segs: r.segs.map(([s, e]) => ({ l: ((s - dayStart) / 86400000) * 100, w: Math.max(((e - s) / 86400000) * 100, 0.4) })),
      }))
      .sort((a, b) => (order[a.type] ?? 9) - (order[b.type] ?? 9) || b.total - a.total);
  }, [data, timelineRange]);

  // Trip periods on the selected day — shaded behind every occupancy bar.
  // Each block keeps its index into `trips` so tapping it jumps to Recent Trips.
  const timelineTripSegs = useMemo(() => {
    if (!timelineRange) return [];
    const nowMs = Date.now();
    return trips
      .map((t, idx) => {
        const a = Math.max(parseTs(t.start_time), timelineRange.start);
        const b = Math.min(t.end_time ? parseTs(t.end_time) : nowMs, timelineRange.end, nowMs);
        return b > a ? { idx, l: ((a - timelineRange.start) / 86400000) * 100, w: Math.max(((b - a) / 86400000) * 100, 0.3) } : null;
      })
      .filter(Boolean);
  }, [trips, timelineRange]);

  // Tap a trip block in the timeline → reveal + highlight that trip in Recent Trips.
  const [highlightTripKey, setHighlightTripKey] = useState(null);
  const [timelineExpanded, setTimelineExpanded] = useState(false);
  const highlightTimerRef = useRef(null);
  const jumpToTrip = (idx) => {
    const trip = trips[idx];
    if (!trip) return;
    if (idx + 1 > Number(tripsLimit)) {
      setTripsLimit(String(['5', '10', '20', '50'].map(Number).find((n) => n >= idx + 1) || 50));
    }
    const key = trip.trip_id || `i${idx}`;
    setHighlightTripKey(key);
    setTimeout(() => {
      document.querySelector(`[data-trip-key="${key}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
    clearTimeout(highlightTimerRef.current);
    highlightTimerRef.current = setTimeout(() => setHighlightTripKey(null), 3000);
  };


  const telemetryRows = useMemo(() => {
    if (!device) return [];
    const flat = flattenIoElements(dev.last_io_elements);
    return (dev.io_mappings || [])
      .filter((m) => m.show_fleet_card !== false)
      .map((m) => ({ m, val: computeDisplayValue(m, flat) }))
      .filter((r) => r.val !== null && r.val !== undefined && r.val !== '');
  }, [device, dev]);

  const fleetCommands = useMemo(() => (device?.custom_commands || []).filter((c) => c.show_fleet_card !== false), [device]);

  const executeSendCommand = async (cmd) => {
    setSendingCommand(true);
    try {
      const targetImei = cmd.target_device_imei || device.imei;
      if (cmd.command_mode === 'sequence' && cmd.command_sequence?.length > 0) {
        await api.post('/parsing/commands/send-sequence', {
          device_imei: targetImei,
          steps: cmd.command_sequence.map((s) => ({ command: s.command_text, delay_ms: s.delay_ms || 0 })),
          button_label: cmd.button_label,
        });
        toast.success(`Sequence "${cmd.button_label}" sent (${cmd.command_sequence.length} steps)`);
      } else {
        await api.post('/parsing/commands/send', { device_imei: targetImei, command: cmd.command_text, command_type: 'custom' });
        toast.success(`Command "${cmd.button_label}" sent`);
      }
      setSendConfirmCmd(null);
    } catch (err) {
      toast.error(`Failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSendingCommand(false);
    }
  };

  if (!device) return null;
  const lat = dev.last_latitude; const lng = dev.last_longitude;
  const lastSeen = dev.last_gps_time || dev.last_seen;
  const ignOn = dev.ignition_on === true;
  const odo = (dev.odometer_source && dev.odometer_source !== 'off' && dev.computed_odometer != null) ? formatDistance(dev.computed_odometer, { decimals: 1 }) : '—';
  const engHrs = (dev.engine_hours_source && dev.engine_hours_source !== 'off' && dev.computed_engine_hours != null) ? `${Number(dev.computed_engine_hours).toFixed(1)} h` : '—';
  const odoSource = (dev.odometer_source && dev.odometer_source !== 'off') ? (dev.odometer_source === 'gps' ? 'GPS calculated' : 'Sensor (IO)') : null;
  const engSource = (dev.engine_hours_source && dev.engine_hours_source !== 'off') ? (dev.engine_hours_source === 'ignition' ? 'Ignition time' : 'Sensor (IO)') : null;
  const maint = data?.maintenance;

  // Explicit GPS fix status (Digital Matter) — same policy as Live Tracking:
  // fresh = valid && !old_fix && age < offline threshold (default 600s)
  const gpsFixChip = (() => {
    const fix = dev.last_gps_fix;
    if (!fix || typeof fix !== 'object') return null;
    const acc = fix.position_accuracy_m != null ? ` · Estimated accuracy: ${fix.position_accuracy_m} m` : '';
    const lastFixT = fix.gps_timestamp ? ` · Last fix: ${formatDT(fix.gps_timestamp)}` : '';
    if (!fix.fix_valid) {
      return { short: 'No valid GPS fix', full: `No valid GPS fix reported${lastFixT ? ` · Last known position retained${lastFixT}` : ''}`, cls: 'text-red-600 border-red-300 bg-red-50 dark:text-red-400 dark:bg-red-950/40' };
    }
    const ageMs = fix.gps_timestamp ? Math.max(0, Date.now() - new Date(fix.gps_timestamp).getTime()) : null;
    const threshold = (dev.offline_threshold_secs || 600) * 1000;
    if (fix.old_fix || ageMs == null || ageMs >= threshold) {
      return { short: `GPS last known · ${fix.gps_timestamp ? timeAgoCap(fix.gps_timestamp) : 'age unknown'}`, full: `Last known GPS position${acc}${lastFixT}`, cls: 'text-amber-700 border-amber-300 bg-amber-50 dark:text-amber-300 dark:bg-amber-950/40' };
    }
    return { short: `GPS fix${fix.fix_3d ? ' · 3D' : ''}${fix.position_accuracy_m != null ? ` · ±${fix.position_accuracy_m}m` : ''}`, full: `GPS fix obtained${fix.fix_3d ? ' · 3D' : ''}${acc}${lastFixT} · Satellites: Not reported`, cls: 'text-blue-600 border-blue-300 bg-blue-50 dark:text-blue-400 dark:bg-blue-950/40' };
  })();

  const eventsSection = canEvents ? (
    <SectionCard
      icon={Bell}
      title="Events"
      testId="quick-view-events-section"
      right={
        <Select value={eventsLimit} onValueChange={setEventsLimit}>
          <SelectTrigger className="h-7 w-[90px] text-xs" data-testid="quick-view-events-limit"><SelectValue /></SelectTrigger>
          <SelectContent>{['10', '20', '50', '100'].map((n) => <SelectItem key={n} value={n}>Last {n}</SelectItem>)}</SelectContent>
        </Select>
      }
    >
      {loading ? (
        <div className="flex items-center gap-2 py-3 justify-center text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /><span className="text-sm">Loading…</span></div>
      ) : events.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2" data-testid="quick-view-events-empty">No events recorded for this vehicle.</p>
      ) : (
        <div className="divide-y rounded-md border max-h-80 overflow-y-auto">
          {events.map((evt) => {
            const sev = evt.severity === 'critical' ? 'text-red-600' : evt.severity === 'warning' ? 'text-amber-600' : 'text-slate-500';
            return (
              <div key={evt.id} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-3 py-2" data-testid={`quick-view-event-${evt.id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <AlertTriangle className={`w-3.5 h-3.5 shrink-0 ${sev}`} />
                  <span className="text-sm font-medium truncate">{evt.event_rule_name || evt.event_type}</span>
                  <Badge variant="outline" className={`text-[10px] shrink-0 capitalize ${evt.status === 'active' ? 'bg-red-100 text-red-600 border-red-200' : 'bg-green-100 text-green-700 border-green-200'}`}>{evt.status}</Badge>
                </div>
                <span className="text-xs text-muted-foreground">{evt.triggered_at ? formatDT(evt.triggered_at) : ''}</span>
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  ) : null;

  const tripsSection = (
    <SectionCard
      icon={Route}
      title={<>Recent Trips {trips.length > 0 && <Badge variant="outline" className="text-xs ml-1" data-testid="quick-view-trips-count">{trips.length} trips in 14d</Badge>}</>}
      testId="quick-view-trips-section"
      right={
        <Select value={tripsLimit} onValueChange={setTripsLimit}>
          <SelectTrigger className="h-7 w-[90px] text-xs" data-testid="quick-view-trips-limit"><SelectValue /></SelectTrigger>
          <SelectContent>{['5', '10', '20', '50'].map((n) => <SelectItem key={n} value={n}>Last {n}</SelectItem>)}</SelectContent>
        </Select>
      }
    >
      {loading ? (
        <div className="flex items-center gap-2 py-4 justify-center text-muted-foreground" data-testid="quick-view-trips-loading"><Loader2 className="w-4 h-4 animate-spin" /><span className="text-sm">Loading trips…</span></div>
      ) : visibleTrips.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2" data-testid="quick-view-trips-empty">No trips in the last 14 days.</p>
      ) : (
        <div className="divide-y rounded-md border max-h-80 overflow-y-auto">
          {visibleTrips.map((t, i) => {
            const occ = tripOccupants(t);
            const tripKey = t.trip_id || `i${i}`;
            return (
              <div
                key={t.trip_id || i}
                data-trip-key={tripKey}
                className={`px-3 py-2.5 space-y-1.5 transition-colors ${highlightTripKey === tripKey ? 'bg-[#2563eb]/10 ring-2 ring-inset ring-[#2563eb]/60' : ''}`}
                data-testid={`quick-view-trip-${i}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                  <div className="min-w-0">
                    <p className="text-sm font-medium flex items-center gap-2">
                      {formatDT(t.start_time)}
                      {t.in_progress && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" data-testid={`quick-view-trip-in-progress-${i}`}>
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> In progress
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">→ {t.in_progress ? 'ongoing' : formatDT(t.end_time)}</p>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-foreground">
                    {t.distance_km != null && <span className="inline-flex items-center gap-1"><Route className="w-3 h-3" /> {Number(t.distance_km).toFixed(1)} km</span>}
                    {t.duration_secs != null && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDuration(t.duration_secs)}</span>}
                    {t.max_speed != null && <span>max {formatSpeed(t.max_speed)}</span>}
                  </div>
                </div>
                {(occ.drivers.length > 0 || occ.occupants.length > 0 || occ.bindings.length > 0) && (
                  <div className="flex flex-wrap items-center gap-1.5" data-testid={`quick-view-trip-occupants-${i}`}>
                    <Users className="w-3.5 h-3.5 text-muted-foreground" />
                    {occ.drivers.map((d) => (
                      <Badge key={`d-${d.person_id}`} variant="outline" className={`text-[10px] border-0 ${OCCUPANCY_BADGE_CLS.driver}`}>Driver: {d.person_name}</Badge>
                    ))}
                    {occ.occupants.map((o) => (
                      <Badge key={`o-${o.person_id}`} variant="outline" className={`text-[10px] border-0 ${OCCUPANCY_BADGE_CLS[o.person_type] || OCCUPANCY_BADGE_CLS.passenger}`}>{OCC_LABEL[o.person_type] || o.person_type}: {o.person_name}</Badge>
                    ))}
                    {occ.bindings.map((b) => (
                      <Badge key={`b-${b.entity_type}-${b.beacon_name}`} variant="outline" className={`text-[10px] border-0 ${OCCUPANCY_BADGE_CLS[b.entity_type] || OCCUPANCY_BADGE_CLS.equipment}`}>{OCC_LABEL[b.entity_type] || b.entity_type}: {b.beacon_name}</Badge>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="p-0 gap-0 w-screen h-[100dvh] max-w-none rounded-none sm:rounded-lg sm:w-[94vw] sm:max-w-6xl sm:h-[92vh] flex flex-col overflow-hidden" data-testid="vehicle-quick-view-dialog">
        <DialogHeader className="px-4 pt-4 pb-3 border-b shrink-0 text-left">
          <div className="flex items-start gap-3">
            {photoUrl ? (
              <button type="button" onClick={() => setPhotoViewerOpen(true)} title="View photo" className="block shrink-0 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563eb]" data-testid="quick-view-photo-btn">
                <img src={photoUrl} alt="Vehicle" className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg object-cover border shrink-0 cursor-zoom-in" data-testid="quick-view-photo" />
              </button>
            ) : (
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg bg-[#2563eb]/10 flex items-center justify-center shrink-0" data-testid="quick-view-photo-placeholder">
                <Camera className="w-7 h-7 text-[#2563eb]/50" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <DialogTitle className="flex flex-wrap items-center gap-2" data-testid="vehicle-quick-view-title">
                {deviceDisplayName(device)}
                {(device.registration || device.imei) && (
                  <span className="text-sm font-normal text-muted-foreground font-mono">{device.registration || device.imei}</span>
                )}
                <Badge variant="outline" className={`text-xs font-normal ${device.is_active ? 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400'}`} data-testid="quick-view-active-badge">
                  {device.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </DialogTitle>
              <DialogDescription className="text-xs">
                {[device.asset_make && `${device.asset_make} ${device.asset_model || ''}`.trim(), device.asset_year, assetTypeLabel(device.asset_type)].filter(Boolean).join(' · ')}
              </DialogDescription>
              <div className="flex flex-wrap items-center gap-1.5 pt-1.5">
                <span className={`${chip} ${ignOn ? 'text-emerald-700 border-emerald-300 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-950/40' : 'text-muted-foreground'}`} data-testid="quick-view-ignition-chip">
                  <Key className="w-3.5 h-3.5" /> Ignition {ignOn ? 'On' : 'Off'}
                </span>
                {device.last_speed != null && (
                  <span className={chip} data-testid="quick-view-speed-chip"><Gauge className="w-3.5 h-3.5" /> {formatSpeed(device.last_speed)}</span>
                )}
                {lastSeen && (
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className={`${chip} cursor-default`} data-testid="quick-view-last-seen-chip"><Clock className="w-3.5 h-3.5" /> {timeAgoCap(lastSeen)}</span>
                      </TooltipTrigger>
                      <TooltipContent className="z-[10001]" data-testid="quick-view-last-seen-tooltip">Last update received: {formatDT(lastSeen)}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {gpsFixChip && (
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className={`${chip} cursor-default ${gpsFixChip.cls}`} data-testid="quick-view-gps-fix-chip"><Satellite className="w-3.5 h-3.5" /> {gpsFixChip.short}</span>
                      </TooltipTrigger>
                      <TooltipContent className="z-[10001] max-w-[300px]" data-testid="quick-view-gps-fix-tooltip">{gpsFixChip.full}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {lat != null && lng != null && (
                  <a href={`https://www.google.com/maps?q=${lat},${lng}`} target="_blank" rel="noopener noreferrer"
                    className={`${chip} text-[#2563eb] border-[#2563eb]/40 hover:bg-[#2563eb]/10 transition-colors`} data-testid="quick-view-gmaps-link">
                    <MapPin className="w-3.5 h-3.5" /> Open in Google Maps <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          </div>
          <div className={`flex flex-wrap ${isMobileWebview ? 'gap-2' : 'gap-1.5'} pt-2`}>
            {onOpenTravelHistory && (
              <Button size="sm" variant="outline" className={actBtn} onClick={onOpenTravelHistory} data-testid="quick-view-travel-history-button">
                <History className={actIcon} /> Travel History
              </Button>
            )}
            {onOpenDriverLogbook && (
              <Button size="sm" variant="outline" className={actBtn} onClick={onOpenDriverLogbook} data-testid="quick-view-logbook-button">
                <BookUser className={actIcon} /> Driver Log Book
              </Button>
            )}
            {onOpenAssetLogbook && (
              <Button size="sm" variant="outline" className={actBtn} onClick={onOpenAssetLogbook} data-testid="quick-view-asset-logbook-button">
                <ClipboardList className={actIcon} /> Asset Log Book
              </Button>
            )}
            {onEdit && device.asset_editing_enabled !== false && (
              <Button size="sm" variant="outline" className={actBtn} onClick={onEdit} data-testid="quick-view-edit-button">
                <Pencil className={actIcon} /> Edit
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" className={actBtn} data-testid="quick-view-export-btn">
                  <Upload className={actIcon} /> Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onSelect={() => exportDashboard('pdf')} data-testid="quick-view-export-pdf">PDF</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => exportDashboard('html')} data-testid="quick-view-export-html">HTML</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {svcStatus && (
              <span className={`${chip} ${svcStatus === 'overdue' ? 'text-red-600 border-red-300 bg-red-50 dark:bg-red-950/40' : 'text-amber-600 border-amber-300 bg-amber-50 dark:bg-amber-950/40'}`} data-testid="quick-view-service-chip">
                <Wrench className="w-3.5 h-3.5" /> {svcStatus === 'overdue' ? 'Service overdue' : 'Service due soon'}
              </span>
            )}
            <span className="ml-auto flex items-center gap-2">
              {lastUpdated && (
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-[10px] text-muted-foreground cursor-default" data-testid="quick-view-updated-ago">Updated {formatTimeAgo(lastUpdated)}</span>
                    </TooltipTrigger>
                    <TooltipContent className="z-[10001]">Data refreshed: {formatDT(lastUpdated)}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              <Button size="sm" variant="outline" className={actBtn} onClick={() => setRefreshNonce((n) => n + 1)} disabled={loading} title="Refresh data (also auto-refreshes every 60s)" data-testid="quick-view-refresh-button">
                <RefreshCw className={`${actIcon} ${loading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </span>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* KPI cards — coloured icon tiles (Dashboard style). Odometer / Engine Hours
              auto-hide when their source is set to OFF in the device settings. */}
          {(() => {
            const showOdo = !!odoSource;
            const showEng = !!engSource;
            const n = 2 + (showOdo ? 1 : 0) + (showEng ? 1 : 0) + (maint ? 1 : 0);
            const grid = { 2: 'lg:grid-cols-2', 3: 'lg:grid-cols-3', 4: 'lg:grid-cols-4', 5: 'lg:grid-cols-5' }[n] || 'lg:grid-cols-5';
            return (
              <div className={`grid grid-cols-2 sm:grid-cols-3 ${grid} gap-2.5`} data-testid="quick-view-kpis">
                {showOdo && <KpiCard label="Odometer" value={odo} sub={odoSource} icon={Gauge} color="bg-blue-500" testId="quick-view-kpi-odometer" />}
                {showEng && <KpiCard label="Engine Hours" value={engHrs} sub={engSource} icon={Timer} color="bg-teal-500" testId="quick-view-kpi-engine-hours" />}
                <KpiCard label="Trips (7d)" value={loading ? '…' : weekStats.trips} icon={Route} color="bg-purple-500" testId="quick-view-kpi-trips" />
                <KpiCard label="Distance (7d)" value={loading ? '…' : `${weekStats.distance.toFixed(1)} km`} icon={MapIcon} color="bg-emerald-500" delta={loading ? null : weekStats.delta} testId="quick-view-kpi-distance" />
                {maint && (
                  <KpiCard
                    label="Maintenance Costs"
                    value={`$${(maint.total_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    sub={`${maint.service_count} service${maint.service_count === 1 ? '' : 's'}`}
                    icon={Wrench} color="bg-orange-500"
                    testId="quick-view-kpi-maintenance"
                  />
                )}
              </div>
            );
          })()}

          {/* Video card — dashcam vehicles only, right after the KPI row */}
          {showVideoCard && (
            <SectionCard icon={Video} title="Video" testId="quick-view-video-section">
              <VehicleVideoCardContent device={dev} showLive={canLiveVideo} showClips={canEventClips} />
            </SectionCard>
          )}

          {/* Card order: Current Trip → Live Occupancy → Last Trip → Driving Behaviour.
              Hidden pieces collapse; an odd trailing card spans the full row width. */}
          {(() => {
            const cardCount = 1 + (currentTrip ? 1 : 0) + (canGreenDriving ? 1 : 0) + ((occupancy.identityEnabled || occupancy.manualDriver || occupancy.rows.length > 0) ? 1 : 0) + (crew.length > 0 ? 1 : 0);
            const lastKey = canGreenDriving ? 'green' : (crew.length > 0 ? 'crew' : 'last');
            const span = (k) => (k === lastKey && cardCount > 1 && cardCount % 2 === 1 ? ' lg:col-span-2' : '');
            return (
          <div className={`grid gap-4 ${cardCount > 1 ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`} data-testid="quick-view-occupancy-row">
            {(occupancy.identityEnabled || occupancy.manualDriver || occupancy.rows.length > 0) && (
            <SectionCard
              icon={Users}
              title="Live Occupancy"
              className="order-2"
              testId="quick-view-occupancy-section"
              right={!occupancy.ignitionOn && occupancy.endedAt ? (
                <span className="text-xs text-muted-foreground" title={formatDT(occupancy.endedAt)}>Trip ended {formatTimeAgo(occupancy.endedAt)}</span>
              ) : null}
            >
              {!occupancy.hasAny ? (
                <p className="text-sm text-muted-foreground py-1" data-testid="quick-view-occupancy-empty">No occupancy recorded.</p>
              ) : (
                <div className="space-y-1.5" data-testid="quick-view-occupancy-chips">
                  {(occupancy.currentDriver || occupancy.manualDriver || (occupancy.ignitionOn && occupancy.identityEnabled) || occupancy.lastDriver) && (
                    <div className="flex items-start justify-between gap-2 text-xs" data-testid="quick-view-occupancy-driver">
                      <span className="text-muted-foreground flex items-center gap-1 shrink-0"><IdCard className="w-3 h-3" /> Driver</span>
                      <div className="flex flex-wrap justify-end items-center gap-1">
                        {occupancy.currentDriver ? (
                          <OccupancyPill rowType="driver" name={occupancy.currentDriver.person_name} personType={occupancy.currentDriver.person_type} source={occupancy.currentDriver.decision_source || occupancy.currentDriver.credential_source} onClick={occupancy.currentDriver.person_id ? () => { onClose(); navigate(`/people?open_person=${occupancy.currentDriver.person_type || 'driver'}:${occupancy.currentDriver.person_id}`); } : undefined} testId="quick-view-occupancy-driver-pill" />
                        ) : occupancy.manualDriver ? (
                          <span
                            className="inline-flex items-center gap-1 rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
                            title="Manually assigned default driver — a real RFID/BLE scan overrides it"
                            data-testid="quick-view-manual-driver-chip"
                          >
                            <UserCog className="h-2.5 w-2.5 shrink-0" />
                            <span className="truncate max-w-[140px]">{occupancy.manualDriver.person_name}</span>
                            <span className="text-[9px] uppercase opacity-70">Assigned</span>
                          </span>
                        ) : occupancy.ignitionOn && occupancy.identityEnabled ? (
                          <span className="inline-flex items-center gap-1 rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-semibold text-orange-700 dark:bg-orange-900/40 dark:text-orange-300" data-testid="quick-view-occupancy-no-driver">
                            <UserX className="h-3 w-3" /> No Driver
                          </span>
                        ) : occupancy.lastDriver ? (
                          <>
                            <span className="text-[10px] text-muted-foreground">Last:</span>
                            <OccupancyPill rowType="driver" name={occupancy.lastDriver.person_name} personType={occupancy.lastDriver.person_type} source={occupancy.lastDriver.decision_source || occupancy.lastDriver.credential_source} last onClick={occupancy.lastDriver.person_id ? () => { onClose(); navigate(`/people?open_person=${occupancy.lastDriver.person_type || 'driver'}:${occupancy.lastDriver.person_id}`); } : undefined} testId="quick-view-occupancy-driver-pill" />
                            {(occupancy.lastDriver.ended_at || occupancy.endedAt) && (
                              <span className="text-[9px] text-muted-foreground/70">· {formatTimeAgo(occupancy.lastDriver.ended_at || occupancy.endedAt)}</span>
                            )}
                          </>
                        ) : null}
                      </div>
                    </div>
                  )}
                  {occupancy.rows.map((r) => {
                    const meta = OCC_ROW_META[r.k]; const Icon = meta.icon;
                    return (
                      <div key={r.k} className="flex items-start justify-between gap-2 text-xs" data-testid={`quick-view-occupancy-${r.k}`}>
                        <span className="text-muted-foreground flex items-center gap-1 shrink-0"><Icon className="w-3 h-3" /> {meta.label}</span>
                        <div className="flex flex-wrap justify-end items-center gap-1">
                          {r.items.length > 0 ? (
                            <>
                              {r.items.map((o, i) => (
                                <OccupancyPill key={i} rowType={r.k} name={o.name} personType={o.person_type} source={o.source} stale={occupancy.offline && (r.k === 'trailer' || r.k === 'equipment')} staleNote={occupancy.offline && (r.k === 'trailer' || r.k === 'equipment') ? `Device offline — attachment last confirmed ${o.last_seen_at ? formatTimeAgo(o.last_seen_at) : 'before the device went offline'}` : undefined} onClick={o.person_id && ['staff', 'passenger'].includes(o.person_type || r.k) ? () => { onClose(); navigate(`/people?open_person=${o.person_type || r.k}:${o.person_id}`); } : undefined} testId={`quick-view-occupancy-${r.k}-pill-${i}`} />
                              ))}
                              {occupancy.offline && (r.k === 'trailer' || r.k === 'equipment') && r.items[0]?.last_seen_at && (
                                <span className="text-[9px] text-muted-foreground/70" data-testid={`quick-view-occupancy-${r.k}-stale-note`}>· last confirmed {formatTimeAgo(r.items[0].last_seen_at)}</span>
                              )}
                            </>
                          ) : (
                            <>
                              <span className="text-[10px] text-muted-foreground">Last:</span>
                              {r.lastItems.map((o, i) => (
                                <OccupancyPill key={i} rowType={r.k} name={o.name} personType={o.person_type} source={o.source} last onClick={o.person_id && ['staff', 'passenger'].includes(o.person_type || r.k) ? () => { onClose(); navigate(`/people?open_person=${o.person_type || r.k}:${o.person_id}`); } : undefined} testId={`quick-view-occupancy-${r.k}-last-pill-${i}`} />
                              ))}
                              {occupancy.endedAt && <span className="text-[9px] text-muted-foreground/70">· {formatTimeAgo(occupancy.endedAt)}</span>}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </SectionCard>
            )}

            {currentTrip && (
              <SectionCard
                icon={Navigation}
                title="Current Trip"
                className="order-1"
                testId="quick-view-current-trip-section"
                right={
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> In progress
                  </span>
                }
              >
                <div className="w-full space-y-1.5" data-testid="quick-view-current-trip-body">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Started</p>
                      <p className="text-sm font-medium">{formatDT(currentTrip.start_time)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Duration</p>
                      <p className="text-sm font-medium" data-testid="quick-view-current-trip-duration">{formatDuration(Math.max(0, Math.round((Date.now() - parseTs(currentTrip.start_time)) / 1000)))}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Distance so far</p>
                      <p className="text-sm font-medium" data-testid="quick-view-current-trip-distance">{currentTrip.distance_km != null ? `${Number(currentTrip.distance_km).toFixed(1)} km` : '—'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Current speed</p>
                      <p className="text-sm font-medium" data-testid="quick-view-current-trip-speed">{dev.last_speed != null ? formatSpeed(dev.last_speed) : '—'}</p>
                    </div>
                  </div>
                  <p className="text-[10px] text-muted-foreground">{currentTrip.synthetic ? 'Trip starting — details syncing · updates every 60s' : 'Updates every 60s'}</p>
                </div>
              </SectionCard>
            )}

            <SectionCard
              icon={Route}
              title="Last Trip"
              className={`order-3${span('last')}`}
              testId="quick-view-last-trip-section"
              right={lastTrip ? <span className="text-xs text-muted-foreground" title={formatDT(lastTrip.end_time)}>Ended {formatTimeAgo(lastTrip.end_time)}</span> : null}
            >
              {loading ? (
                <div className="flex items-center gap-2 py-1 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /><span className="text-sm">Loading…</span></div>
              ) : !lastTrip ? (
                <p className="text-sm text-muted-foreground py-1" data-testid="quick-view-last-trip-empty">No completed trips in the last 14 days.</p>
              ) : (() => {
                const occ = tripOccupants(lastTrip);
                return (
                  <div className="space-y-1.5">
                    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{formatDT(lastTrip.start_time)}</p>
                        <p className="text-xs text-muted-foreground">→ {formatDT(lastTrip.end_time)}</p>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-foreground">
                        {lastTrip.distance_km != null && <span className="inline-flex items-center gap-1"><Route className="w-3 h-3" /> {Number(lastTrip.distance_km).toFixed(1)} km</span>}
                        {lastTrip.duration_secs != null && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDuration(lastTrip.duration_secs)}</span>}
                        {lastTrip.max_speed != null && <span>max {formatSpeed(lastTrip.max_speed)}</span>}
                      </div>
                    </div>
                    {(occ.drivers.length > 0 || occ.occupants.length > 0 || occ.bindings.length > 0) ? (
                      <div className="flex flex-wrap items-center gap-1.5" data-testid="quick-view-last-trip-occupants">
                        <Users className="w-3.5 h-3.5 text-muted-foreground" />
                        {occ.drivers.map((d) => (
                          <Badge key={`d-${d.person_id}`} variant="outline" className={`text-[10px] border-0 ${OCCUPANCY_BADGE_CLS.driver}`}>Driver: {d.person_name}</Badge>
                        ))}
                        {occ.occupants.map((o) => (
                          <Badge key={`o-${o.person_id}`} variant="outline" className={`text-[10px] border-0 ${OCCUPANCY_BADGE_CLS[o.person_type] || OCCUPANCY_BADGE_CLS.passenger}`}>{OCC_LABEL[o.person_type] || o.person_type}: {o.person_name}</Badge>
                        ))}
                        {occ.bindings.map((b) => (
                          <Badge key={`b-${b.entity_type}-${b.beacon_name}`} variant="outline" className={`text-[10px] border-0 ${OCCUPANCY_BADGE_CLS[b.entity_type] || OCCUPANCY_BADGE_CLS.equipment}`}>{OCC_LABEL[b.entity_type] || b.entity_type}: {b.beacon_name}</Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground" data-testid="quick-view-last-trip-no-occupants">No occupants detected for this trip.</p>
                    )}
                  </div>
                );
              })()}
            </SectionCard>

            {/* Crew & Occupants — square person photos (People-list style), click to enlarge.
                Shows the people currently aboard; falls back to the LAST trip's people. */}
            {crew.length > 0 && (
              <SectionCard
                icon={Users}
                title="Crew & Occupants"
                className={`order-4${span('crew')}`}
                testId="quick-view-crew-photos-section"
                right={crew[0]?.last ? (
                  <span className="text-xs text-muted-foreground" title={occupancy.endedAt ? formatDT(occupancy.endedAt) : undefined}>
                    Last trip{occupancy.endedAt ? ` · ended ${formatTimeAgo(occupancy.endedAt)}` : ''}
                  </span>
                ) : null}
              >
                <div className="flex flex-wrap gap-4" data-testid="quick-view-crew-photos">
                  {crew.map((p, i) => (
                    <CrewPhotoTile key={`${p.role}-${p.person_id || p.name}-${i}`} api={api} person={p} index={i} onNameClick={p.person_id ? () => { onClose(); navigate(`/people?open_person=${p.person_type || p.role}:${p.person_id}`); } : undefined} onNavigate={(path) => { onClose(); navigate(path); }} />
                  ))}
                </div>
              </SectionCard>
            )}

            {canGreenDriving && (
              <SectionCard icon={Leaf} title="Driving Behaviour — Last 7 Days" right={<DrivingBehaviourHelp />} className={`order-5${span('green')}`} testId="quick-view-green-driving-section">
                {loading ? (
                  <div className="flex items-center gap-2 py-1 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /><span className="text-sm">Loading…</span></div>
                ) : (
                  <div className="space-y-1.5" data-testid="quick-view-green-driving-stats">
                    <div className="grid grid-cols-1 sm:grid-cols-[150px_1fr] gap-3 items-center">
                      {/* Behaviour Score donut — same sizing as the Status Distribution donut */}
                      <div className="relative h-36" data-testid="quick-view-behaviour-donut">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={hbDonut} dataKey="value" nameKey="name" innerRadius={34} outerRadius={55} paddingAngle={hbDonut.length > 1 ? 2 : 0} startAngle={90} endAngle={-270}>
                              {hbDonut.map((s) => <Cell key={s.key} fill={s.key === 'clean' ? '#10b981' : HB_COLORS[s.key]} />)}
                            </Pie>
                            {harshTotal > 0 && <ReTooltip {...TT_PROPS} formatter={(v, n) => [v, n]} />}
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                          <span className={`text-xl font-bold leading-none ${hbScoreCls}`} data-testid="quick-view-behaviour-score">{hbScore ?? '—'}</span>
                          <span className="text-[10px] text-muted-foreground mt-0.5">{hbRating === 'not_rated' ? 'Not rated' : 'Score'}</span>
                        </div>
                      </div>
                      <div className="space-y-2 min-w-0">
                        {harshTotal === 0 ? (
                          <p className="text-sm text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5" data-testid="quick-view-green-driving-clean">
                            <Leaf className="w-4 h-4" /> No harsh driving events in the last 7 days.
                          </p>
                        ) : (
                          <div className="grid grid-cols-3 gap-2">
                            {[
                              ['harsh_braking', 'Harsh Braking'],
                              ['harsh_acceleration', 'Harsh Acceleration'],
                              ['harsh_cornering', 'Harsh Cornering'],
                            ].map(([k, label]) => (
                              <div key={k} className="rounded-lg px-2 py-1.5 text-center shadow-sm" style={{ backgroundColor: HB_COLORS[k] }} data-testid={`quick-view-green-${k}`}>
                                <p className="text-lg font-bold leading-tight text-white">{harsh[k] || 0}</p>
                                <p className="text-[10px] leading-tight text-white/90">{label}</p>
                              </div>
                            ))}
                          </div>
                        )}
                        {hbRating !== 'rated' && behaviour && (
                          <p className="text-[11px] text-muted-foreground" data-testid="quick-view-behaviour-rating-note">
                            {hbRating === 'not_rated'
                              ? `Not yet rated — ${behaviour.km} km driven in the last 7 days is under the ${behaviour.thresholds?.min_rated_km ?? 50} km minimum (change in Settings).`
                              : `Low confidence — ${behaviour.km} km driven in the last 7 days is under ${behaviour.thresholds?.low_confidence_km ?? 200} km (change in Settings).`}
                          </p>
                        )}
                        {hbDaily.length > 0 && (
                          <div className="h-20" data-testid="quick-view-behaviour-trend">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={hbDaily} margin={{ top: 2, right: 2, left: -26, bottom: 0 }}>
                                <XAxis dataKey="label" tick={{ fontSize: 9 }} />
                                <YAxis tick={{ fontSize: 9 }} allowDecimals={false} />
                                <ReTooltip {...TT_PROPS} cursor={BAR_CURSOR} />
                                <Bar dataKey="harsh_braking" name="Harsh Braking" stackId="hb" fill={HB_COLORS.harsh_braking} />
                                <Bar dataKey="harsh_acceleration" name="Harsh Acceleration" stackId="hb" fill={HB_COLORS.harsh_acceleration} />
                                <Bar dataKey="harsh_cornering" name="Harsh Cornering" stackId="hb" fill={HB_COLORS.harsh_cornering} radius={[2, 2, 0, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        )}
                        <p className="text-[11px] text-muted-foreground" data-testid="quick-view-green-rate">
                          {harshTotal} event{harshTotal === 1 ? '' : 's'} in 7 days
                          {behaviour && <> · {behaviour.events_per_100km} per 100 km</>}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {occupancy.identityEnabled && !loading && (stats?.trips_7d ?? 0) > 0 && (
                  <div className="mt-2.5 pt-2.5 border-t border-border/60" data-testid="quick-view-driver-allocation">
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">Drivers — {stats.trips_7d} trips in 7 days</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 bg-slate-200/80 dark:bg-slate-900 dark:border dark:border-slate-800" data-testid="quick-view-drivers-allocated">
                        <UserCheck className="w-5 h-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                        <div className="min-w-0">
                          <p className="text-base font-bold leading-tight text-emerald-700 dark:text-emerald-300">{stats?.trips_with_driver_7d ?? 0}</p>
                          <p className="text-[10px] leading-tight text-emerald-700/80 dark:text-emerald-300/80">Allocated</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 bg-slate-200/80 dark:bg-slate-900 dark:border dark:border-slate-800" data-testid="quick-view-drivers-unallocated">
                        <UserX className="w-5 h-5 shrink-0 text-amber-600 dark:text-amber-400" />
                        <div className="min-w-0">
                          <p className="text-base font-bold leading-tight text-amber-700 dark:text-amber-300">{stats?.trips_without_driver_7d ?? 0}</p>
                          <p className="text-[10px] leading-tight text-amber-700/80 dark:text-amber-300/80">Not allocated</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </SectionCard>
            )}
          </div>
            );
          })()}

          {/* Assignment History — who set the manual driver and when (device audit trail) */}
          {assignHistory.length > 0 && (
          <SectionCard icon={UserCog} title="Assignment History" testId="quick-view-assignment-history">
            <div className="space-y-1.5">
              {(assignHistoryExpanded ? assignHistory : assignHistory.slice(0, 5)).map((h, i) => (
                <div key={i} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 text-xs border-b border-border/40 last:border-0 pb-1.5 last:pb-0" data-testid={`quick-view-assignment-history-row-${i}`}>
                  <span className="min-w-0 flex-1">{h.details}</span>
                  <span className="text-muted-foreground whitespace-nowrap shrink-0">
                    {h.timestamp ? formatDT(h.timestamp) : ''}{h.actor_email ? ` · ${h.actor_email}` : ''}
                  </span>
                </div>
              ))}
              {assignHistory.length > 5 && (
                <button type="button" className="text-xs text-[#2563eb] hover:underline" onClick={() => setAssignHistoryExpanded((v) => !v)} data-testid="quick-view-assignment-history-toggle">
                  {assignHistoryExpanded ? 'Show less' : `Show all ${assignHistory.length}`}
                </button>
              )}
            </div>
          </SectionCard>
          )}

          {/* Occupancy Timeline — who/what was aboard through the selected day */}
          {occupancy.identityEnabled && (
          <SectionCard
            icon={Clock}
            title="Occupancy Timeline"
            testId="quick-view-occupancy-timeline"
            right={
              <Select value={tDay} onValueChange={(v) => { setTimelineDay(v); setTimelineExpanded(false); }}>
                <SelectTrigger className="h-7 w-[150px] text-xs" data-testid="quick-view-timeline-day-select"><SelectValue /></SelectTrigger>
                <SelectContent>{timelineDays.map((d) => <SelectItem key={d.v} value={d.v}>{d.label}</SelectItem>)}</SelectContent>
              </Select>
            }
          >
            {loading ? (
              <div className="flex items-center gap-2 py-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /><span className="text-sm">Loading…</span></div>
            ) : timelineRows.length === 0 && timelineTripSegs.length === 0 ? (
              <p className="text-sm text-muted-foreground py-1" data-testid="quick-view-timeline-empty">No occupancy recorded for this day.</p>
            ) : (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <span className="w-24 sm:w-28 shrink-0" />
                  <div className="flex-1 relative h-4 text-[9px] text-muted-foreground">
                    {[0, 6, 12, 18].map((h) => (
                      <span key={h} className="absolute" style={{ left: `${(h / 24) * 100}%` }}>{String(h).padStart(2, '0')}:00</span>
                    ))}
                    <span className="absolute right-0">24:00</span>
                  </div>
                  <span className="w-12 shrink-0" />
                </div>
                {timelineTripSegs.length > 0 && (
                  <div className="flex items-center gap-2" data-testid="quick-view-timeline-trips-row">
                    <span className="w-24 sm:w-28 shrink-0 truncate text-[11px] font-medium text-[#2563eb]">Trips</span>
                    <div className="flex-1 relative h-3.5 rounded bg-muted/60 overflow-hidden">
                      {timelineTripSegs.map((s, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => jumpToTrip(s.idx)}
                          className="absolute top-0 bottom-0 rounded-sm bg-[#2563eb]/50 hover:bg-[#2563eb]/80 transition-colors cursor-pointer"
                          style={{ left: `${s.l}%`, width: `${s.w}%`, minWidth: 8 }}
                          aria-label="View trip details"
                          data-testid={`quick-view-timeline-trip-block-${i}`}
                        />
                      ))}
                    </div>
                    <span className="w-12 shrink-0 text-right text-[10px] text-muted-foreground">{timelineTripSegs.length}</span>
                  </div>
                )}
                {/* Rows: first 8 shown; "Show all" expands into a scrollable area (~16 rows max)
                    so the card never dominates the dialog. Axis + Trips strip stay pinned above. */}
                <div className={timelineExpanded ? 'space-y-1.5 max-h-[22rem] overflow-y-auto pr-1' : 'space-y-1.5'} data-testid="quick-view-timeline-rows">
                  {(timelineExpanded ? timelineRows : timelineRows.slice(0, 8)).map((r) => (
                    <div key={`${r.type}:${r.name}`} className="flex items-center gap-2" data-testid={`quick-view-timeline-row-${r.type}`}>
                      <span className="w-24 sm:w-28 shrink-0 truncate text-[11px] font-medium" style={{ color: TIMELINE_COLORS[r.type] }}>{r.name}</span>
                      <div className="flex-1 relative h-3.5 rounded bg-muted/60 overflow-hidden">
                        {timelineTripSegs.map((s, i) => (
                          <span key={`t${i}`} className="absolute top-0 bottom-0" style={{ left: `${s.l}%`, width: `${s.w}%`, backgroundColor: TRIP_OVERLAY_COLOR }} />
                        ))}
                        {r.segs.map((s, i) => (
                          <span key={i} className="absolute top-0 bottom-0 rounded-sm" style={{ left: `${s.l}%`, width: `${s.w}%`, backgroundColor: TIMELINE_COLORS[r.type] }} />
                        ))}
                      </div>
                      <span className="w-12 shrink-0 text-right text-[10px] text-muted-foreground">{formatDuration(Math.round(r.total / 1000))}</span>
                    </div>
                  ))}
                </div>
                {timelineRows.length > 8 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-full text-xs text-muted-foreground"
                    onClick={() => setTimelineExpanded((v) => !v)}
                    data-testid="quick-view-timeline-show-all"
                  >
                    {timelineExpanded ? 'Show less' : `Show all ${timelineRows.length}`}
                  </Button>
                )}
                <div className="flex flex-wrap items-center gap-3 pt-1" data-testid="quick-view-timeline-legend">
                  {timelineTripSegs.length > 0 && (
                    <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                      <span className="w-2 h-2 rounded-sm bg-[#2563eb]/50" /> Trip period (tap to view)
                    </span>
                  )}
                  {[...new Set(timelineRows.map((r) => r.type))].map((t) => (
                    <span key={t} className="flex items-center gap-1 text-[10px] text-muted-foreground">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: TIMELINE_COLORS[t] }} /> {TIMELINE_LABEL[t] || t}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </SectionCard>
          )}

          {/* Telemetry — swapped with Commands (user request) */}
          {canTelemetry && telemetryRows.length > 0 && (
            <SectionCard icon={Gauge} title="Telemetry" testId="quick-view-telemetry-section">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1">
                {telemetryRows.map(({ m, val }) => (
                  <div key={m.id} className="flex justify-between items-center py-0.5 text-sm border-b border-dashed border-border/50 last:border-0" data-testid={`quick-view-io-${m.id}`}>
                    <span className="text-xs text-muted-foreground truncate max-w-[55%]">{m.display_name}</span>
                    <span className={`text-xs font-semibold ${getIoValueColorClass(val, m.value_colors)}`}>{val}</span>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          {/* Charts + status donut */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="quick-view-charts">
            <Card>
              <CardContent className="p-3">
                <p className="text-xs font-semibold mb-2">Daily Distance (km) — Last 7 Days</p>
                <div className="h-36">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dailySeries} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <ReTooltip {...TT_PROPS} cursor={BAR_CURSOR} formatter={(v) => [`${v} km`, 'Distance']} />
                      <Bar dataKey="km" fill="#2563eb" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3">
                <p className="text-xs font-semibold mb-2">Daily Driving Time (mins) — Last 7 Days</p>
                <div className="h-36">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dailySeries} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <ReTooltip {...TT_PROPS} cursor={BAR_CURSOR} formatter={(v) => [`${v} mins`, 'Driving']} />
                      <Bar dataKey="mins" fill="#0d9488" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            <Card data-testid="quick-view-status-donut">
              <CardContent className="p-3">
                <p className="text-xs font-semibold mb-2">Status Distribution (hrs) — Last 7 Days</p>
                <div className="h-36 flex items-center">
                  <ResponsiveContainer width="55%" height="100%">
                    <PieChart>
                      <Pie data={statusDonut} dataKey="value" nameKey="name" innerRadius={34} outerRadius={55} paddingAngle={2}>
                        {statusDonut.map((s) => <Cell key={s.name} fill={DONUT_COLORS[s.name]} />)}
                      </Pie>
                      <ReTooltip {...TT_PROPS} formatter={(v, n) => [`${v} h`, n]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-1 text-xs flex-1">
                    {statusDonut.map((s) => (
                      <div key={s.name} className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: DONUT_COLORS[s.name] }} />{s.name}</span>
                        <span className="font-medium">{s.value} h</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Commands — swapped with Telemetry (user request), directly under the graphs */}
          {canCommands && fleetCommands.length > 0 && (
            <SectionCard icon={Send} title="Commands" testId="quick-view-commands-section">
              <div className={`flex flex-wrap justify-center ${isMobileWebview ? 'gap-3' : 'gap-2'}`}>
                {fleetCommands.map((cmd) => (
                  <Button
                    key={cmd.id}
                    size="sm"
                    className={`${isMobileWebview ? 'h-14 w-full text-base font-semibold rounded-lg' : 'h-9 w-full sm:w-52'} text-white ${CMD_BG[cmd.button_color] || CMD_BG.blue}`}
                    disabled={sendingCommand && sendConfirmCmd?.id === cmd.id}
                    onClick={() => (cmd.requires_confirmation ? setSendConfirmCmd(cmd) : executeSendCommand(cmd))}
                    title={cmd.description || cmd.command_text}
                    data-testid={`quick-view-cmd-${cmd.id}`}
                  >
                    {sendingCommand && sendConfirmCmd?.id === cmd.id
                      ? <Loader2 className={isMobileWebview ? 'w-5 h-5 animate-spin mr-2' : 'w-3.5 h-3.5 animate-spin mr-1.5'} />
                      : <Send className={isMobileWebview ? 'w-5 h-5 mr-2' : 'w-3.5 h-3.5 mr-1.5'} />}
                    <span className="truncate">{cmd.button_label}</span>
                  </Button>
                ))}
              </div>
            </SectionCard>
          )}

          {/* Maintenance table */}
          {maint && (
            <SectionCard
              icon={Wrench}
              title="Maintenance History"
              testId="quick-view-maintenance-section"
              right={
                <Button size="sm" variant="ghost" className="h-7 text-xs text-[#2563eb]" onClick={() => { onClose(); navigate(`/fleet?tab=maintenance&svc_device=${device.id}`); }} data-testid="quick-view-maintenance-link">
                  View all in Maintenance <ExternalLink className="w-3 h-3 ml-1" />
                </Button>
              }
            >
              {(maint.history || []).length === 0 ? (
                <p className="text-sm text-muted-foreground py-2" data-testid="quick-view-maintenance-empty">No service history recorded.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Date</TableHead>
                      <TableHead className="text-xs">Task</TableHead>
                      <TableHead className="text-xs text-right">Cost</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(maint.history || []).map((h, i) => (
                      <TableRow key={i} data-testid={`quick-view-maintenance-row-${i}`}>
                        <TableCell className="text-xs py-2">{h.completed_date ? String(h.completed_date).slice(0, 10) : '—'}</TableCell>
                        <TableCell className="text-sm py-2 font-medium">{h.task_name || 'Service'}</TableCell>
                        <TableCell className="text-sm py-2 text-right">{h.cost != null ? `$${Number(h.cost).toFixed(2)}` : '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </SectionCard>
          )}

          {/* Trips & Events — side by side on wide screens; Trips takes the full
              width when the Events card is hidden (no events privilege) */}
          <div className={`grid grid-cols-1 ${canEvents ? 'lg:grid-cols-2' : ''} gap-4`} data-testid="quick-view-bottom-row">
            {tripsSection}
            {eventsSection}
          </div>

          {/* Documents — read-only list (view/download); uploads live in Edit Asset Details */}
          {vehicleDocs.length > 0 && (
            <SectionCard icon={Paperclip} title={`Documents (${vehicleDocs.length})`} testId="quick-view-documents-section">
              <ReadOnlyAttachmentsList attachments={vehicleDocs} docTypes={VEHICLE_DOC_TYPES} testPrefix="quick-view-doc" />
              <p className="text-[11px] text-muted-foreground mt-2">View or download only — add or edit documents in Edit Asset Details.</p>
            </SectionCard>
          )}
        </div>

        {/* Command confirmation dialog */}
        <Dialog open={!!sendConfirmCmd} onOpenChange={(o) => { if (!o) setSendConfirmCmd(null); }}>
          <DialogContent className="sm:max-w-md" data-testid="quick-view-cmd-confirm-dialog">
            {sendConfirmCmd && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" /> Confirm Command</DialogTitle>
                  <DialogDescription>
                    Send "{sendConfirmCmd.button_label}" to {deviceDisplayName(device)}?
                    {sendConfirmCmd.description ? ` ${sendConfirmCmd.description}` : ''}
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" size="sm" className={isMobileWebview ? 'h-12 text-base' : ''} onClick={() => setSendConfirmCmd(null)} data-testid="quick-view-cmd-cancel">Cancel</Button>
                  <Button size="sm" className={`${isMobileWebview ? 'h-12 text-base' : ''} text-white ${CMD_BG[sendConfirmCmd.button_color] || CMD_BG.blue}`} disabled={sendingCommand} onClick={() => executeSendCommand(sendConfirmCmd)} data-testid="quick-view-cmd-confirm">
                    {sendingCommand ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Send className="w-3.5 h-3.5 mr-1.5" />} Send
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Vehicle photo viewer — same pattern as the Person Dashboard photo modal */}
        <Dialog open={photoViewerOpen && !!photoUrl} onOpenChange={setPhotoViewerOpen}>
          <DialogContent className="max-w-xl" data-testid="vehicle-photo-viewer">
            <DialogHeader>
              <DialogTitle>{deviceDisplayName(device)}</DialogTitle>
              <DialogDescription>{device.registration || device.imei || 'Vehicle photo'}</DialogDescription>
            </DialogHeader>
            {photoUrl && <img src={photoUrl} alt={deviceDisplayName(device)} className="w-full max-h-[65vh] object-contain rounded-md border" data-testid="vehicle-photo-viewer-img" />}
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};
